import { defineStore } from "pinia"
import { ref, computed } from "vue"
import Cookies from "js-cookie"
import MetaService from "@/services/resources/MetaService"
import NPSService from "@/services/resources/NPSResearchService"
import { useStateStore } from "./state.store"
import { useUserStore } from "./user.store"

/* Services */
const serviceMeta = MetaService.build()
const serviceNps = NPSService.build()

/* Utilities */
const defineMainColor = (paramColor: string | null | undefined) => {
  const isNull = !paramColor || paramColor === "null"
  const color = isNull ? "#F58220" : paramColor
  const el = document
  el.documentElement.setAttribute("data-color", color)
  const headTag = document.getElementsByTagName("head")[0]
  const styleTag = document.createElement("style")
  styleTag.innerHTML = `
        :root {
            --maincolor: ${color}
        }
  `
  headTag.appendChild(styleTag)
  const doc = document.documentElement
  doc.setAttribute("data-color", color)
  doc.style.setProperty("--maincolor", color)
  doc.style.setProperty("--maincolortrans", color + "0f")
}

const transformOnToBoolean = (obj: Record<string, unknown>): Record<string, unknown> => {
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      if (obj[key] === "on") {
        obj[key] = true
      } else if (obj[key] === "off") {
        obj[key] = false
      }
    }
  }
  return obj
}

export interface ConfigSettings {
  gamefication?: boolean | null
  home_vertical?: boolean
  without_category?: boolean
  enable_category?: boolean
  hide_courses?: boolean
  home_carousel?: boolean
  rating_home?: boolean
  free_courses?: boolean
  google_gtm?: boolean
  google_gtm_code?: string
  learning_progression?: boolean
  [key: string]: unknown
}

export const useConfigStore = defineStore('config', () => {
  /* State */
  const settings = ref<ConfigSettings>({})
  const theme = ref<string>("light")
  const admin = ref<string | null>(null)
  const loading = ref<boolean>(true)
  const gtm = ref<string>("")
  const linkResearchNPS = ref<string>("")

  /* Getters (computed) */
  const getSettings = computed(() => settings.value)
  const getGamificationStatus = computed(() => settings.value.gamefication ?? null)
  const getIsGamificationActive = computed(() => settings.value.gamefication === true)
  const getIsAdmin = computed(() => ["admin", "owner"].includes(Cookies.get("access_level") ?? ""))
  const getIsLoading = computed(() => loading.value)
  const getMovieCoverMode = computed(() => settings.value?.home_vertical)
  const getModuleViewMode = computed(() => settings.value?.without_category)
  const getCategoryViewMode = computed(() => !settings.value?.without_category)
  const getEnabledCategory = computed(() => settings.value?.enable_category)
  const getHideDisabledCourses = computed(() => settings.value?.hide_courses)
  const getModeCarousel = computed(() => settings.value?.home_carousel)
  const getShowRating = computed(() => settings.value?.rating_home)
  const getLinkResearchNPS = computed(() => linkResearchNPS.value)
  const isFreeCoursesActive = computed(() => settings.value.free_courses)

  /* Actions */
  function setLoading(value: boolean) {
    loading.value = value
  }

  function setModeAdmin(value: string | null) {
    admin.value = value
  }

  function setConfig(newSettings: ConfigSettings) {
    settings.value = { ...settings.value, ...newSettings }
  }

  function setMainColor(color: string | null | undefined) {
    defineMainColor(color)
  }

  function setGtm(value: string) {
    gtm.value = value
  }

  function setNpsLink(link: string) {
    linkResearchNPS.value = link
  }

  function actionSetIsLoading(value: boolean) {
    loading.value = value
  }

  function actionDefineMainColor() {
    // VSUS-1754: com as settings carregadas antes do mount, o AppLayout
    // sobrescrevia a cor do site com o padrao (localStorage vazio para alunos).
    const fromSettings = settings.value?.main_color as string | undefined
    const storage = localStorage.getItem("mainColor")
    defineMainColor(fromSettings || storage)
  }

  async function actionSaveSettings(config: Record<string, unknown>) {
    await serviceMeta.postID(config)
    const response = await actionFetchSettings()
    return response
  }

  async function actionSaveConditionalLinks(links: unknown) {
    await serviceMeta.postID({
      id: "conditional-links",
      links,
    })
    const response = await actionFetchSettings()
    return response
  }

  async function actionSaveHideCoursesIds(ids: unknown) {
    await serviceMeta.postID({
      id: "hide_courses_ids",
      course_ids: ids,
    })
    const response = await actionFetchSettings()
    return response
  }

  async function actionFetchMetas({ keys }: { keys: string[] }) {
    const keysPath = keys.map((key) => `keys[]=${key}`).join("&")
    const response = await serviceMeta.search(keysPath)
    return transformOnToBoolean(response)
  }

  async function actionFetchNPSResearch() {
    delayLoaded(async () => {
      try {
        const response = await serviceNps.search()
        if (response?.link) {
          linkResearchNPS.value = response.link
        }
      } catch (error) {
        console.error('[NPS] Error:', error)
      }
    })
  }

  async function actionFetchSettings(key?: string) {
    const settingsKeys = key
      ? [key]
      : [
        "study_schedule_allowed",
        "exercises_allowed",
        "introductoryVideo",
        "rating_home",
        "coment_lesson",
        "comment_review",
        "helper_data",
        "external_help",
        "external_help_link",
        "limit_devices_per_member",
        "jivochat",
        "whatsapp",
        "bitrix",
        "id_jivochat",
        "id_whatsapp",
        "whatsapp_code",
        "whatsapp_number",
        "whatsapp_message",
        "id_bitrix",
        "termsOfUse",
        "termsOfUseActivated",
        "first_access_enabled",
        "first_access_config",
        "limit_percetage_lesson",
        "number_limit_percetage_lesson",
        "complete_lessson_next",
        "number_limit_devices_per_member",
        "protected_font_size",
        "onboarding",
        "main_color",
        "without_category",
        "enable_category",
        "home_carousel",
        "home_vertical",
        "view_course",
        "hide_courses",
        "theme_fixed_painel",
        "theme_fixed_painel_color",
        "learning_progression",
        "login_background_image",
        "link_to_privacy_policy",
        "link_to_user_terms",
        "link_menu_1_icon",
        "link_menu_1_label",
        "link_menu_1_url",
        "link_menu_1_newtab",
        "link_menu_1_conditional",
        "link_menu_1_type",
        "link_menu_1_signature_ids",
        "link_menu_1_course_class_ids",
        "link_menu_2_icon",
        "link_menu_2_label",
        "link_menu_2_url",
        "link_menu_2_newtab",
        "link_menu_2_conditional",
        "link_menu_2_type",
        "link_menu_2_signature_ids",
        "link_menu_2_course_class_ids",
        "link_menu_3_icon",
        "link_menu_3_label",
        "link_menu_3_url",
        "link_menu_3_newtab",
        "link_menu_3_conditional",
        "link_menu_3_type",
        "link_menu_3_signature_ids",
        "link_menu_3_course_class_ids",
        "link_menu_4_icon",
        "link_menu_4_label",
        "link_menu_4_url",
        "link_menu_4_newtab",
        "link_menu_4_conditional",
        "link_menu_4_type",
        "link_menu_4_signature_ids",
        "link_menu_4_course_class_ids",
        "logomarca",
        "logo",
        "pais",
        "google_gtm",
        "google_gtm_code",
        "gamefication",
        "free_courses",
        "feature_cancel_subscription",
      ]

    const mapSettings = settingsKeys.map((k) => `keys[]=${k}`).join("&")
    const response = await serviceMeta.search(mapSettings)

    if (response?.google_gtm === true) {
      gtm.value = response.google_gtm_code
    }

    const isAdmin = Cookies.get("access_level")
    if (isAdmin) {
      admin.value = isAdmin
    }

    const transformed = transformOnToBoolean(response) as ConfigSettings
    settings.value = { ...settings.value, ...transformed }

    useStateStore().patchSettings(transformed)
    useUserStore().setMetasSite(response)

    delayLoaded(() => {
      actionFetchNPSResearch()
    })

    return response
  }

  function $reset() {
    settings.value = {}
    theme.value = "light"
    admin.value = null
    loading.value = true
    gtm.value = ""
    linkResearchNPS.value = ""
  }

  return {
    /* State */
    settings,
    theme,
    admin,
    loading,
    gtm,
    linkResearchNPS,

    /* Getters */
    getSettings,
    getGamificationStatus,
    getIsGamificationActive,
    getIsAdmin,
    getIsLoading,
    getMovieCoverMode,
    getModuleViewMode,
    getCategoryViewMode,
    getEnabledCategory,
    getHideDisabledCourses,
    getModeCarousel,
    getShowRating,
    getLinkResearchNPS,
    isFreeCoursesActive,

    /* Actions */
    setLoading,
    setModeAdmin,
    setConfig,
    setMainColor,
    setGtm,
    setNpsLink,
    actionSetIsLoading,
    actionDefineMainColor,
    actionSaveSettings,
    actionSaveConditionalLinks,
    actionSaveHideCoursesIds,
    actionFetchMetas,
    actionFetchNPSResearch,
    actionFetchSettings,
    $reset,
  }
})
